import React, { useState } from "react";
import { useWordAddinAuth } from "./WordAddinApp";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Zap, ArrowRight, Mail, Globe } from "lucide-react";

// Translations
const translations = {
  nb: {
    title: "Logg inn i Lexy",
    subtitle: "Juridisk AI-assistent for Word",
    emailLabel: "E-post",
    emailPlaceholder: "din@epost.no",
    passwordLabel: "Passord",
    passwordPlaceholder: "••••••••",
    loginButton: "Logg inn",
    loggingIn: "Logger inn...",
    orContinueWith: "eller fortsett med",
    google: "Google",
    microsoft: "Microsoft",
    demo: "Demo",
    language: "Språk"
  },
  en: {
    title: "Sign in to Lexy",
    subtitle: "Legal AI assistant for Word",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    loginButton: "Sign in",
    loggingIn: "Signing in...",
    orContinueWith: "or continue with",
    google: "Google",
    microsoft: "Microsoft",
    demo: "Demo",
    language: "Language"
  }
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [language, setLanguage] = useState<'nb' | 'en'>(() => {
    return (localStorage.getItem('lexy_plugin_lang') as 'nb' | 'en') || 'nb';
  });
  const { login, loginWithGoogle, loginWithMicrosoft } = useWordAddinAuth();

  const t = translations[language];

  const handleLanguageChange = (lang: 'nb' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('lexy_plugin_lang', lang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    try {
      await loginWithMicrosoft();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="login-page">
      
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 flex items-center gap-1 z-10">
        <Globe className="w-4 h-4 text-slate-400" />
        <button
          onClick={() => handleLanguageChange('nb')}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            language === 'nb' 
              ? 'bg-slate-900 text-white' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          NO
        </button>
        <button
          onClick={() => handleLanguageChange('en')}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            language === 'en' 
              ? 'bg-slate-900 text-white' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          EN
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[320px]" data-testid="login-form">
          
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 mb-4 shadow-lg shadow-violet-500/25">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
              {t.title}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {t.subtitle}
            </p>
          </div>

          {!showEmailForm ? (
            /* Social Login Buttons */
            <div className="space-y-3">
              {/* Google Login */}
              <Button 
                variant="outline"
                className="w-full h-11 justify-center gap-3 border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium text-slate-700">{t.google}</span>
              </Button>

              {/* Microsoft Login */}
              <Button 
                variant="outline"
                className="w-full h-11 justify-center gap-3 border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                onClick={handleMicrosoftLogin}
                disabled={loading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z"/>
                  <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                  <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                  <path fill="#FFB900" d="M13 13h10v10H13z"/>
                </svg>
                <span className="font-medium text-slate-700">{t.microsoft}</span>
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-slate-400">{t.orContinueWith}</span>
                </div>
              </div>

              {/* Email Login Button */}
              <Button 
                variant="outline"
                className="w-full h-11 justify-center gap-2 border-slate-200 hover:bg-slate-50 transition-all"
                onClick={() => setShowEmailForm(true)}
              >
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-slate-700">{t.emailLabel}</span>
              </Button>
            </div>
          ) : (
            /* Email Login Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 bg-slate-50 border-slate-200 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                  data-testid="email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t.passwordLabel}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t.passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 bg-slate-50 border-slate-200 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                  data-testid="password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium transition-all" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.loggingIn}
                  </>
                ) : (
                  <>
                    {t.loginButton}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {/* Back button */}
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="w-full text-sm text-slate-500 hover:text-slate-700 mt-2"
              >
                ← {language === 'nb' ? 'Tilbake' : 'Back'}
              </button>
            </form>
          )}

          {/* Demo credentials */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-xs text-center text-slate-400">
              {t.demo}: <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[11px]">hallo@hallo.no</code>
              {" / "}
              <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[11px]">Password123!</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default LoginPage;
